import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, RefreshCw, Package, Check, ChevronDown, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { AddressSuggestInput } from "./AddressViaInput";
import { BoxInfoAccordion } from "./BoxInfoAccordion";
import { useAuth } from "../context/AuthContext";
import { BOX_PRICE, BOX_MAX_VALUE, BOX_MIN_PRODUCTS, BOX_MIN_VALUE, euro } from "../lib/box";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
  const { items, open, setOpen, plan, setPlan, updateQty, removeItem, clear, subtotal, count } = useCart();
  const { user, checkAuth } = useAuth();
  const [code, setCode] = useState("");
  const [codeApplied, setCodeApplied] = useState(false);
  const [addr, setAddr] = useState({ via: "", civico: "", citta: "", provincia: "", cap: "", telefono: "" });
  const [addrCoords, setAddrCoords] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [frequency, setFrequency] = useState("monthly");
  const [useCredit, setUseCredit] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  useEffect(() => {
    if (user?.address?.via) {
      setAddr({
        via: user.address.via || "", civico: user.address.civico || "", citta: user.address.citta || "",
        provincia: user.address.provincia || "", cap: user.address.cap || "",
        telefono: user.address.telefono || "",
      });
    }
    if (user?.email) setEmail((e) => e || user.email);
    if (user?.name) {
      const parts = user.name.trim().split(/\s+/);
      setFirstName((v) => v || parts[0] || "");
      setLastName((v) => v || parts.slice(1).join(" "));
    }
  }, [user]);

  // Se il carrello cambia e non rispetta più le regole del Box, torna all'acquisto singolo
  useEffect(() => {
    if (plan === "box_mensile" && (items.length < BOX_MIN_PRODUCTS || subtotal > BOX_MAX_VALUE)) {
      setPlan("single");
      toast(`Carrello modificato: il Box richiede almeno ${BOX_MIN_PRODUCTS} prodotti diversi e fino a ${euro(BOX_MAX_VALUE)} di valore`);
    }
  }, [items, subtotal, plan, setPlan]);

  const addrComplete = Boolean(addr.via.trim() && addr.civico.trim() && addr.citta.trim() && addr.provincia.trim() && addr.cap.trim() && addr.telefono.trim());
  const [step, setStep] = useState("cart"); // cart | done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const isBox = plan === "box_mensile";
  const shipping = isBox || subtotal > 69 ? 0 : 4.9;
  const welcomeDiscount = codeApplied && !isBox ? subtotal * 0.10 : 0;
  const userCredit = !isBox && user ? Number(user.credit || 0) : 0;
  const creditApplied = useCredit && userCredit > 0 ? Math.round(Math.min(userCredit, Math.max(0, subtotal - welcomeDiscount + shipping - 0.5)) * 100) / 100 : 0;
  const total = isBox ? BOX_PRICE : Math.max(0, subtotal - welcomeDiscount - creditApplied + shipping);

  const applyCode = () => {
    if (user && code.trim().toUpperCase() === user.welcome_code && !user.welcome_used) {
      setCodeApplied(true);
      toast.success("Codice applicato: -10%");
    } else {
      setCodeApplied(false);
      toast.error("Codice non valido o già utilizzato");
    }
  };

  const checkout = async () => {
    const missing = [];
    if (!firstName.trim() || !lastName.trim()) { missing.push("nome e cognome"); setDataOpen(true); }
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { missing.push("un'email valida"); setDataOpen(true); }
    if (!addr.via.trim() || !addr.civico.trim() || !addr.citta.trim() || !addr.provincia.trim() || !addr.cap.trim() || !addr.telefono.trim()) {
      missing.push("l'indirizzo completo (via, civico, comune, provincia, CAP, telefono)");
      setAddrOpen(true);
    }
    if (!acceptedTerms) missing.push("l'accettazione delle Condizioni di Vendita");
    if (missing.length) {
      setCheckoutOpen(true);
      toast.error(`Per andare al pagamento servono: ${missing.join(", ")}`);
      return;
    }
    if (isBox && subtotal < BOX_MIN_VALUE - 0.001) {
      setCheckoutOpen(true);
      toast.error(`Il valore complessivo dei prodotti nel carrello per la Box è ancora inferiore ai ${euro(BOX_MIN_VALUE)} minimi consentiti per accedere al pagamento, che ha la quota fissa di ${euro(BOX_PRICE)}. Aggiungi altri prodotti per procedere.`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty })),
        plan,
        frequency: isBox ? frequency : null,
        email: email.trim(),
        customer_name: `${firstName.trim()} ${lastName.trim()}`,
        code: codeApplied ? user.welcome_code : null,
        use_credit: creditApplied > 0,
        address: addr,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Pagamento non avviato, riprova");
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep("cart"), 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="safe-pt safe-pb fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-cream shadow-2xl sm:max-w-3xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center gap-3 border-b border-ink/10 px-6 py-5">
              <button onClick={close} data-testid="cart-back-button" aria-label="Torna indietro" className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink/15 text-ink transition-colors hover:bg-ink hover:text-cream">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <h3 className="min-w-0 flex-1 font-serif text-2xl text-ink">
                {step === "done" ? "Ordine confermato" : "Il tuo carrello"}
              </h3>
              <button onClick={close} data-testid="close-cart-button" className="grid h-9 w-9 place-items-center rounded-full text-ink transition-colors hover:bg-ink/10">
                <X className="h-5 w-5" strokeWidth={1.6} />
              </button>
            </div>

            {step === "done" ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="order-success">
                <span className="grid h-16 w-16 place-items-center rounded-full bg-sage text-cream">
                  <Check className="h-7 w-7" strokeWidth={2} />
                </span>
                <h4 className="font-serif text-3xl text-ink">Grazie!</h4>
                <p className="text-sm text-ink-soft">
                  Il tuo ordine è stato registrato (checkout simulato).
                  {plan === "box_mensile" ? " Il tuo Box Mensile è attivo." : ""}
                </p>
                <button onClick={close} className="mt-2 rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-cream transition-transform hover:scale-[1.04]">
                  Continua lo shopping
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty">
                <Package className="h-10 w-10 text-ink-soft" strokeWidth={1.2} />
                <p className="text-ink-soft">Il carrello è vuoto.</p>
                <button onClick={close} className="rounded-full border border-ink/20 px-6 py-3 text-sm text-ink hover:bg-ink hover:text-cream">
                  Esplora i prodotti
                </button>
              </div>
            ) : (
              <>
                <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
                <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-6 py-4">
                  {/* Plan toggle */}
                  <div className="mb-5 grid grid-cols-2 gap-2 rounded-full bg-ink/5 p-1" data-testid="plan-toggle">
                    <button
                      onClick={() => setPlan("single")}
                      data-testid="plan-single"
                      className={`rounded-full py-2.5 text-xs font-semibold transition-colors ${plan === "single" ? "bg-ink text-cream" : "text-ink-soft"}`}
                    >
                      Acquisto singolo
                    </button>
                    <button
                      onClick={() => {
                        if (items.length < BOX_MIN_PRODUCTS) { toast(`Il Box richiede almeno ${BOX_MIN_PRODUCTS} prodotti diversi nel carrello`); return; }
                        if (subtotal > BOX_MAX_VALUE) { toast(`Il Box arriva fino a ${euro(BOX_MAX_VALUE)} di prodotti: ora ne hai ${euro(subtotal)}`); return; }
                        setPlan("box_mensile");
                      }}
                      data-testid="plan-box"
                      className={`flex items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-semibold transition-colors ${plan === "box_mensile" ? "bg-electric text-cream" : "text-ink-soft"}`}
                    >
                      <RefreshCw className="h-3 w-3" strokeWidth={2} /> Box Mensile {euro(BOX_PRICE)}/mese
                    </button>
                  </div>
                  {isBox && (
                    <div className="mb-4" data-testid="cart-frequency-selector">
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-soft">Ricevi il box</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[{ v: "monthly", l: "Ogni mese" }, { v: "bimonthly", l: "Ogni 2 mesi" }].map((o) => (
                          <button key={o.v} type="button" onClick={() => setFrequency(o.v)} data-testid={`cart-frequency-${o.v}`}
                            className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${frequency === o.v ? "border-electric bg-electric/15 text-ink" : "border-ink/15 text-ink-soft hover:border-ink/30"}`}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Articoli — sezione riducibile */}
                  <button
                    onClick={() => setItemsOpen(!itemsOpen)}
                    data-testid="items-toggle"
                    className="mb-4 flex w-full items-center justify-between rounded-2xl border border-ink/10 bg-panel px-5 py-3.5"
                  >
                    <span className="text-xs font-semibold uppercase tracking-overline text-ink-soft">Articoli ({count})</span>
                    <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                      {euro(subtotal)}
                      <ChevronDown className={`h-4 w-4 text-ink-soft transition-transform ${itemsOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                    </span>
                  </button>
                  {itemsOpen && (
                  <div className="space-y-3">
                    {items.map((it) => (
                      <div key={it.id} className="flex gap-3" data-testid={`cart-item-${it.id}`}>
                        <div className="clip-frame h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl">
                          <img src={it.image} alt={it.name} className="h-full w-full object-contain bg-white p-1" />
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex justify-between gap-2">
                            <h5 className="font-serif text-sm leading-tight text-ink">{it.name}</h5>
                            <button onClick={() => removeItem(it.id)} data-testid={`remove-item-${it.id}`} className="text-ink-soft hover:text-destructive">
                              <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                            </button>
                          </div>
                  <span className="mt-0.5 text-xs text-ink-soft">{euro(it.price)}</span>
                          <div className="mt-auto flex items-center gap-3">
                            <div className="flex items-center gap-3 rounded-full border border-ink/15 px-2 py-0.5">
                              <button onClick={() => updateQty(it.id, it.qty - 1)} data-testid={`decrease-${it.id}`} className="text-ink hover:text-sage"><Minus className="h-3.5 w-3.5" strokeWidth={2} /></button>
                              <span className="w-4 text-center text-sm font-medium" data-testid={`qty-${it.id}`}>{it.qty}</span>
                              <button onClick={() => updateQty(it.id, it.qty + 1)} data-testid={`increase-${it.id}`} className="text-ink hover:text-sage"><Plus className="h-3.5 w-3.5" strokeWidth={2} /></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>

                <div className="shrink-0 border-t border-ink/10 sm:w-[380px] sm:border-l sm:border-t-0" data-testid="checkout-section">
                  <button type="button" onClick={() => setCheckoutOpen(!checkoutOpen)} data-testid="checkout-toggle" className="flex w-full items-center justify-between gap-3 px-6 py-4 sm:hidden">
                    <span className="text-sm text-ink-soft">Totale <span className="text-base font-semibold text-electric" data-testid="cart-total-bar">{euro(total)}</span></span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-electric">
                      Dati e pagamento
                      <ChevronDown className={`h-4 w-4 transition-transform ${checkoutOpen ? "" : "rotate-180"}`} strokeWidth={2} />
                    </span>
                  </button>
                  <div className={`${checkoutOpen ? "" : "hidden"} max-h-[70dvh] overflow-y-auto px-6 pb-4 pt-1 sm:block sm:max-h-none sm:py-3.5`}>
                  <div className="mb-3 overflow-hidden rounded-2xl border border-ink/15 bg-panel" data-testid="section-customer-data">
                    <button type="button" onClick={() => setDataOpen(!dataOpen)} data-testid="toggle-customer-data" className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left">
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink">I tuoi dati *</span>
                        <span className="block truncate text-xs text-ink-soft">
                          {firstName.trim() && email.trim() ? `${firstName} ${lastName} · ${email}` : "Nome, cognome ed email per la conferma"}
                        </span>
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-electric transition-transform ${dataOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                    </button>
                    {dataOpen && (
                      <div className="space-y-2 border-t border-ink/10 px-4 pb-4 pt-3">
                        <div className="flex gap-2">
                          <input
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Nome *"
                            data-testid="checkout-first-name"
                            autoComplete="given-name"
                            className="min-w-0 flex-1 rounded-full border border-ink/15 bg-panel px-5 py-3 text-sm outline-none focus:border-electric"
                          />
                          <input
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Cognome *"
                            data-testid="checkout-last-name"
                            autoComplete="family-name"
                            className="min-w-0 flex-1 rounded-full border border-ink/15 bg-panel px-5 py-3 text-sm outline-none focus:border-electric"
                          />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="La tua email * (conferma ordine)"
                          data-testid="checkout-email"
                          autoComplete="email"
                          className="w-full rounded-full border border-ink/15 bg-panel px-5 py-3 text-sm outline-none focus:border-electric"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3 overflow-hidden rounded-2xl border border-ink/15 bg-panel" data-testid="section-address">
                    <button type="button" onClick={() => setAddrOpen(!addrOpen)} data-testid="add-address-toggle" className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left">
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink">Indirizzo di spedizione *</span>
                        <span className="block truncate text-xs text-ink-soft" data-testid="address-summary-text">
                          {addrComplete ? `${addr.via} ${addr.civico}, ${addr.cap} ${addr.citta} (${addr.provincia})` : "Dove consegniamo il tuo pacco"}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {addrComplete && <Check className="h-4 w-4 text-electric" strokeWidth={2.5} />}
                        <ChevronDown className={`h-4 w-4 text-electric transition-transform ${addrOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                      </span>
                    </button>
                    {addrOpen && (
                    <div className="space-y-2 border-t border-ink/10 px-4 pb-4 pt-3" data-testid="checkout-address">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-overline text-ink-soft">Indirizzo di spedizione *</p>
                        <span className="flex gap-3">
                          {addrComplete && (
                            <button onClick={() => setAddrOpen(false)} data-testid="confirm-address" className="text-xs font-semibold text-electric hover:underline">Conferma e riduci</button>
                          )}
                          {(addr.via || addr.citta) && (
                            <button onClick={() => setAddr({ via: "", civico: "", citta: "", provincia: "", cap: "", telefono: "" })} data-testid="remove-address" className="text-xs text-ink-soft hover:text-destructive">Svuota</button>
                          )}
                        </span>
                      </div>
                      <AddressSuggestInput
                        mode="city"
                        value={addr.citta}
                        onChange={(v) => { setAddr({ ...addr, citta: v }); setAddrCoords(null); }}
                        onSelect={(s) => { setAddr({ ...addr, citta: s.citta, provincia: s.provincia || addr.provincia }); if (s.lat) setAddrCoords({ lat: s.lat, lon: s.lon }); }}
                        testid="addr-citta"
                        placeholder="Comune di residenza (inizia da qui)"
                        className="w-full rounded-full border border-ink/15 bg-panel px-5 py-2.5 text-sm outline-none focus:border-electric"
                      />
                      <div className="flex gap-2">
                        <div className="min-w-0 flex-1">
                          <AddressSuggestInput
                            value={addr.via}
                            onChange={(v) => setAddr({ ...addr, via: v })}
                            onSelect={(s) => setAddr({ ...addr, via: s.via, civico: s.civico || addr.civico, citta: s.citta || addr.citta, provincia: s.provincia || addr.provincia })}
                            citta={addr.citta}
                            coords={addrCoords}
                            testid="addr-via"
                            placeholder="Via (es. Via Roma)"
                            className="w-full rounded-full border border-ink/15 bg-panel px-5 py-2.5 text-sm outline-none focus:border-electric"
                          />
                        </div>
                        <input value={addr.civico} onChange={(e) => setAddr({ ...addr, civico: e.target.value })} placeholder="N. civico" data-testid="addr-civico" className="w-24 shrink-0 rounded-full border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-electric" />
                      </div>
                      <div className="flex gap-2">
                        <input value={addr.provincia} onChange={(e) => setAddr({ ...addr, provincia: e.target.value })} placeholder="Provincia" data-testid="addr-provincia" className="flex-1 rounded-full border border-ink/15 bg-panel px-5 py-2.5 text-sm outline-none focus:border-electric" />
                        <AddressSuggestInput
                          mode="cap"
                          value={addr.cap}
                          onChange={(v) => setAddr({ ...addr, cap: v.replace(/\D/g, "").slice(0, 5) })}
                          onSelect={(s) => setAddr({ ...addr, cap: s.cap, citta: addr.citta || s.citta || "", provincia: addr.provincia || s.provincia || "" })}
                          citta={addr.citta}
                          coords={addrCoords}
                          testid="addr-cap"
                          placeholder="CAP"
                          className="w-28 rounded-full border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-electric"
                        />
                      </div>
                      <input value={addr.telefono} onChange={(e) => setAddr({ ...addr, telefono: e.target.value })} placeholder="Telefono * (serve al corriere)" data-testid="addr-tel" autoComplete="tel" className="w-full rounded-full border border-ink/15 bg-panel px-5 py-2.5 text-sm outline-none focus:border-electric" />
                    </div>
                    )}
                  </div>
                  {user && !user.welcome_used && !isBox && (
                    <div className="mb-3 overflow-hidden rounded-2xl border border-ink/15 bg-panel" data-testid="section-promo">
                      <button type="button" onClick={() => setPromoOpen(!promoOpen)} data-testid="toggle-promo" className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left">
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink">Codice sconto</span>
                          <span className="block truncate text-xs text-ink-soft">{codeApplied ? "Codice -10% applicato" : "Hai un codice? Inseriscilo qui"}</span>
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-electric transition-transform ${promoOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                      </button>
                      {promoOpen && (
                        <div className="border-t border-ink/10 px-4 pb-4 pt-3">
                          <div className="flex gap-2">
                            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice sconto" data-testid="promo-input" className="flex-1 rounded-full border border-ink/15 bg-panel px-5 py-3 text-sm outline-none focus:border-electric" />
                            <button onClick={applyCode} data-testid="promo-apply" className="rounded-full border border-ink/15 px-4 text-sm font-medium text-ink hover:bg-ink hover:text-cream">Applica</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {codeApplied && <p className="mb-3 text-xs font-semibold text-electric" data-testid="promo-ok">Codice applicato: -10%</p>}
                  {!isBox && userCredit > 0 && (
                    <button type="button" onClick={() => setUseCredit(!useCredit)} data-testid="use-credit-toggle"
                      className={`mb-3 flex w-full items-center justify-between rounded-2xl border px-5 py-3.5 text-left text-sm font-semibold transition-colors ${useCredit ? "border-electric bg-electric/10 text-electric" : "border-ink/15 text-ink"}`}>
                      <span>Usa il tuo credito: {euro(userCredit)}</span>
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${useCredit ? "border-electric bg-electric text-cream" : "border-ink/25"}`}>
                        {useCredit && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>
                    </button>
                  )}
                  {!isBox && (subtotal < 69 ? (
                    <div className="mb-3" data-testid="freeship-bar">
                      <p className="mb-1 text-xs text-ink-soft">Aggiungi <span className="font-semibold text-electric">{euro(69 - subtotal)}</span> per la spedizione gratis</p>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-ink/10"><div className="h-full rounded-full bg-electric transition-all" style={{ width: `${Math.min(100, (subtotal / 69) * 100)}%` }} /></div>
                    </div>
                  ) : (
                    <p className="mb-3 text-xs font-semibold text-electric" data-testid="freeship-bar">Spedizione gratis sbloccata!</p>
                  ))}
                  <div className="space-y-1.5 text-sm text-ink-soft">
                    <div className="flex justify-between"><span>{isBox ? `Valore prodotti (${count})` : `Subtotale (${count})`}</span><span data-testid="cart-subtotal">{euro(subtotal)}</span></div>
                    {isBox && <div className="flex justify-between text-electric" data-testid="box-fixed-line"><span>Prezzo Box Mensile (fisso)</span><span>{euro(BOX_PRICE)}/mese</span></div>}
                    {isBox && subtotal > BOX_PRICE && <div className="flex justify-between font-medium text-electric" data-testid="box-savings-line"><span>Risparmi rispetto al listino</span><span>-{euro(subtotal - BOX_PRICE)}</span></div>}
                    {isBox && subtotal < BOX_PRICE && subtotal >= BOX_MIN_VALUE - 0.001 && <p className="text-xs text-amber" data-testid="box-fill-hint">Paghi comunque {euro(BOX_PRICE)}: aggiungi prodotti fino a {euro(BOX_MAX_VALUE)} di valore!</p>}
                    {isBox && subtotal < BOX_MIN_VALUE - 0.001 && <p className="text-xs font-semibold text-red-600" data-testid="box-min-value-hint">Valore box {euro(subtotal)}: sotto i {euro(BOX_MIN_VALUE)} minimi non puoi procedere al pagamento. Aggiungi altri prodotti!</p>}
                    {isBox && <BoxInfoAccordion />}
                    {welcomeDiscount > 0 && <div className="flex justify-between text-electric" data-testid="welcome-discount-line"><span>Sconto benvenuto -10%</span><span>-{euro(welcomeDiscount)}</span></div>}
                    {creditApplied > 0 && <div className="flex justify-between text-electric" data-testid="credit-line"><span>Credito utilizzato</span><span>-{euro(creditApplied)}</span></div>}
                    <div className="flex justify-between"><span>Spedizione</span><span className={shipping === 0 ? "font-semibold text-electric" : ""}>{shipping === 0 ? "Gratis" : euro(shipping)}</span></div>
                  </div>
                  <div className="mt-3 flex justify-between border-t border-ink/10 pt-3 text-lg font-semibold text-ink">
                    <span>Totale</span><span data-testid="cart-total" className="text-electric">{euro(total)}</span>
                  </div>
                  <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl border border-ink/10 bg-panel px-3.5 py-3" data-testid="accept-terms-label">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      data-testid="accept-terms-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#00A3FF]"
                    />
                    <span className="text-xs leading-snug text-ink-soft">
                      Ho letto e accetto le <a href="/termini" target="_blank" className="font-medium text-ink underline decoration-electric/50 underline-offset-2">Condizioni di Vendita</a> e la <a href="/privacy" target="_blank" className="font-medium text-ink underline decoration-electric/50 underline-offset-2">Privacy Policy</a> *
                    </span>
                  </label>
                  <button
                    onClick={checkout}
                    disabled={loading}
                    data-testid="checkout-button"
                    className="mt-4 w-full rounded-full bg-sage py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.02] hover:bg-sage-deep active:scale-95 disabled:opacity-60"
                  >
                    {loading ? "Reindirizzo al pagamento…" : plan === "box_mensile" ? "Attiva abbonamento e paga" : "Vai al pagamento sicuro"}
                  </button>
                  <p className="mt-2 text-center text-[11px] text-ink-soft">Pagamento sicuro con Stripe · Carte, Apple Pay, Google Pay</p>
                  </div>
                </div>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
